import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Button, IconButton, Skeleton, Spinner } from '../primitives/primitives.js';
import { Icon, type IconName } from '../icons/icons.js';
import styles from './feedback.module.css';

/** TESSMA ONE D-8 feedback and application states — STATUS: APPROVED AND FROZEN (v1.1). */

export type Tone = 'success' | 'warning' | 'danger' | 'info';

const ALERT_CLASS: Record<Tone, string> = {
  success: styles.alertSuccess ?? '',
  warning: styles.alertWarning ?? '',
  danger: styles.alertDanger ?? '',
  info: styles.alertInfo ?? '',
};

const TOAST_CLASS: Record<Tone, string> = {
  success: styles.toastSuccess ?? '',
  warning: styles.toastWarning ?? '',
  danger: styles.toastDanger ?? '',
  info: styles.toastInfo ?? '',
};

const TOAST_ICON_CLASS: Record<Tone, string> = {
  success: styles.toastIconSuccess ?? '',
  warning: styles.toastIconWarning ?? '',
  danger: styles.toastIconDanger ?? '',
  info: styles.toastIconInfo ?? '',
};

/* §16 semantic vocabulary. Drawn icons, never characters standing in for them. */
const TONE_ICON: Record<Tone, IconName> = {
  success: 'check-circle',
  warning: 'warning',
  danger: 'warning-circle',
  info: 'info',
};

/* ------------------------------------------------------------------ */
/* Inline alert                                                        */
/* ------------------------------------------------------------------ */

export interface AlertProps {
  readonly tone: Tone;
  readonly title?: string;
  readonly actions?: ReactNode;
  readonly onDismiss?: () => void;
  readonly children?: ReactNode;
}

export function Alert({ tone, title, actions, onDismiss, children }: AlertProps) {
  return (
    <div
      className={`${styles.alert} ${ALERT_CLASS[tone]}`}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <span className={styles.alertIcon}>
        <Icon name={TONE_ICON[tone]} size={15} />
      </span>
      <div className={styles.alertBody}>
        {title !== undefined && <p className={styles.alertTitle}>{title}</p>}
        {children !== undefined && <p className={styles.alertText}>{children}</p>}
        {actions !== undefined && <div className={styles.alertActions}>{actions}</div>}
      </div>
      {onDismiss !== undefined && (
        <IconButton label="Dismiss" size="sm" bare onClick={onDismiss}>
          <Icon name="x" size={14} />
        </IconButton>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Toast                                                               */
/* ------------------------------------------------------------------ */

export interface ToastMessage {
  readonly id: string;
  readonly tone: Tone;
  readonly title: string;
  readonly text?: string;
}

interface ToastContextValue {
  readonly toasts: ReadonlyArray<ToastMessage>;
  readonly push: (toast: Omit<ToastMessage, 'id'>) => void;
  readonly dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<ReadonlyArray<ToastMessage>>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = `toast-${String(Date.now())}-${String(Math.random()).slice(2, 8)}`;
    setToasts((current) => [...current, { ...toast, id }]);
  }, []);

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === null) throw new Error('useToast must be used inside a ToastProvider');
  return context;
}

/** A polite live region, so a toast is announced rather than only seen. */
export function ToastRegion({
  toasts,
  onDismiss,
}: {
  readonly toasts: ReadonlyArray<ToastMessage>;
  readonly onDismiss: (id: string) => void;
}) {
  return (
    <div className={styles.toastRegion} role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${styles.toast} ${TOAST_CLASS[toast.tone]}`}
          role={toast.tone === 'danger' ? 'alert' : 'status'}
        >
          <span className={`${styles.alertIcon} ${TOAST_ICON_CLASS[toast.tone]}`}>
            <Icon name={TONE_ICON[toast.tone]} size={15} />
          </span>
          <div className={styles.toastBody}>
            <p className={styles.toastTitle}>{toast.title}</p>
            {toast.text !== undefined && <p className={styles.toastText}>{toast.text}</p>}
          </div>
          <IconButton
            label="Dismiss notification"
            size="sm"
            bare
            onClick={() => onDismiss(toast.id)}
          >
            <Icon name="x" size={14} />
          </IconButton>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Application states                                                  */
/* ------------------------------------------------------------------ */

interface StateProps {
  readonly title: string;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  readonly compact?: boolean;
}

function StateBlock({
  icon,
  glyph,
  danger = false,
  title,
  description,
  actions,
  compact = false,
  live,
}: StateProps & {
  /** A name from the frozen §16 vocabulary. */
  readonly icon?: IconName;
  /** For the one state whose mark is not an icon: the loading spinner. */
  readonly glyph?: ReactNode;
  readonly danger?: boolean;
  readonly live?: 'polite' | 'assertive';
}) {
  return (
    <div
      className={[styles.state, compact ? styles.stateCompact : ''].filter(Boolean).join(' ')}
      role={live === undefined ? undefined : live === 'assertive' ? 'alert' : 'status'}
    >
      <span
        className={[styles.stateGlyph, danger ? styles.stateGlyphDanger : '']
          .filter(Boolean)
          .join(' ')}
        aria-hidden="true"
      >
        {icon === undefined ? glyph : <Icon name={icon} size={20} />}
      </span>
      <h3 className={styles.stateTitle}>{title}</h3>
      {description !== undefined && <p className={styles.stateText}>{description}</p>}
      {actions !== undefined && <div className={styles.stateActions}>{actions}</div>}
    </div>
  );
}

export function EmptyState(props: StateProps & { readonly icon?: IconName }) {
  const { icon = 'tray', ...rest } = props;
  return <StateBlock icon={icon} {...rest} />;
}

export function NoResultsState({
  onClear,
  ...props
}: StateProps & { readonly onClear?: () => void }) {
  return (
    <StateBlock
      icon="magnifying-glass"
      live="polite"
      {...props}
      actions={
        props.actions ??
        (onClear === undefined ? undefined : (
          <Button variant="secondary" onClick={onClear}>
            Clear filters
          </Button>
        ))
      }
    />
  );
}

export function ErrorState({ onRetry, ...props }: StateProps & { readonly onRetry?: () => void }) {
  return (
    <StateBlock
      icon="warning-circle"
      danger
      live="assertive"
      {...props}
      actions={
        props.actions ??
        (onRetry === undefined ? undefined : (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        ))
      }
    />
  );
}

export function LoadingState({
  title = 'Loading…',
  description,
  compact = false,
}: Partial<StateProps>) {
  return (
    <StateBlock
      glyph={<Spinner size={18} />}
      title={title}
      description={description}
      compact={compact}
      live="polite"
    />
  );
}

/** Skeleton placeholder shaped like the content it stands in for. */
export function SkeletonRows({
  rows = 5,
  withAvatar = false,
}: {
  readonly rows?: number;
  readonly withAvatar?: boolean;
}) {
  return (
    <div className={styles.skeletonRows} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div className={styles.skeletonRow} key={index}>
          {withAvatar && <Skeleton width={24} height={24} radius={12} />}
          <Skeleton width="26%" height={11} />
          <Skeleton width="38%" height={11} />
          <Skeleton width="14%" height={11} />
        </div>
      ))}
    </div>
  );
}
